import { ExclamationTriangleIcon } from '@interstate/components/Icons/ExclamationTriangleIcon';
import { Spinner as LoadingIndicator } from '@interstate/components/Spinner';
import { Tooltip } from '@interstate/components/Tooltip';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as manualFieldOverridesSelectors from '@makemydeal/dr-dash-store/selectors/manualFieldOverridesSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { CDL_USER_INTENT } from '@makemydeal/dr-dash-store/constants';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { PaymentGridCellItem, PaymentGridRow } from '@makemydeal/dr-platform-types';
import { FINANCE } from '@makemydeal/dr-platform-types';
import { PaymentGridSource, resolveColumnVerdict } from '@makemydeal/dr-offer-redux';
import { shallowEqual, useDispatch, useSelector, useStore } from 'react-redux';
import { CALCULATED_COLUMN_ICON_COLOR, CALCULATED_COLUMN_TOOLTIP } from '../../constants';
import { useFieldOverrideIndicator } from '../../hooks/useFieldOverrideIndicator';
import { DownPaymentInputField } from './components/DownPaymentInputField';
import { useIsAspPaymentGridRedesignActive } from './hooks/useIsAspPaymentGridRedesignActive';
import { useIsColumnCalculated } from './hooks/useIsColumnCalculated';
import {
    CalculatedCellIconButton,
    CalculatedCellRow,
    LoadingIndicatorWrapper,
    StyledGridItem,
    StyledTypography
} from './styled.components';
import { hasTermAndDownpayment } from './utils';

interface IGridRowProps {
    data: PaymentGridRow;
    isLoading?: boolean;
    hasFailure?: boolean;
}

const GridRow = ({ data, isLoading, hasFailure }: IGridRowProps) => {
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const dealXgId = useSelector(offerReduxSelectors.getDealXgId);
    const dispatch = useDispatch();
    const isAspPgRedesignEnabled = useIsAspPaymentGridRedesignActive();
    const isExplicitLenderP1Enabled = useSelector(featureToggleSelectors.isExplicitLenderP1Enabled);
    const isPgOverrideIndicatorEnabled = useSelector(featureToggleSelectors.isPgOverrideIndicatorEnabled);
    const { recordManualOverride: recordBaseResidualOverride } = useFieldOverrideIndicator('baseResidual');
    const { recordManualOverride: recordSellRateOverride } = useFieldOverrideIndicator('sellRate');
    const paymentGridTerms = useSelector(offerReduxSelectors.getPaymentGridTerms);
    const paymentGridCashDown = useSelector(offerReduxSelectors.getPaymentGridCashDown);
    const columnSources = useSelector(offerReduxSelectors.getPaymentGridColumnSources);
    const decidedColumns = useSelector(offerReduxSelectors.getPaymentGridDecidedColumns);
    const erroredColumns = useSelector(offerReduxSelectors.getPaymentGridErroredColumns, shallowEqual);
    const hasCellFailure = (index: number) => (isAspPgRedesignEnabled ? erroredColumns.includes(index) : hasFailure);
    const verdictFor = (index: number, termMonths: number | null) =>
        resolveColumnVerdict(decidedColumns, columnSources, index, termMonths);
    /**
     * The Lease grid's per-column verdict. Kept beside `columnSources`, because the two predicates below are Finance's
     * and must keep reading the raw map. A payment needs a verdict saying *this build quoted it*, not merely a term
     * record left over in `terms.byId`, which is merge-only and outlives the grid that produced it.
     */
    const columnView = useSelector(offerReduxSelectors.getPaymentGridColumnView);
    /**
     * When each cell of this grid last priced. The verdict the comment above asks for, for the suppression
     * below: `RESET_PAYMENT_GRID` drops these with the axes they date, so they cannot outlive their grid the
     * way a term record can.
     */
    const cellPricedAt = useSelector((state: any) => offerReduxSelectors.getPaymentGridCellPricedAt(state, offerType));
    const store = useStore();
    const termAt = (index: number) => paymentGridTerms?.[index];
    /** This row's own cash down, or undefined once the row has been cleared. The single test for a cleared row. */
    const rowCashDown = typeof data.val === 'number' ? data.val : undefined;

    const isCalculatedColumn = useIsColumnCalculated();
    const isColumnCalculated = (item: PaymentGridCellItem, index: number) =>
        isCalculatedColumn(index, termAt(index), !isCellLoading(item, index));

    /** An undecided column's first response may be replaced by the fallback pass, so it is held per column; once a term has a source this defers to item.isLoading. */
    const isColumnDecided = (index: number) => {
        const termMonths = paymentGridTerms?.[index];
        if (!termMonths) return true;
        return verdictFor(index, termMonths) !== undefined;
    };

    /** A failing row's own isLoading clears when its response lands, so an empty cell on any non-`Unavailable` source still counts as resolving. */
    const isColumnAwaitingOutcome = (item: PaymentGridCellItem, index: number) => {
        if (!isAspPgRedesignEnabled || !item.isEmpty) return false;
        if (rowCashDown === undefined) return false;
        const termMonths = paymentGridTerms?.[index];
        if (!termMonths) return false;
        const source = verdictFor(index, termMonths);
        return source !== undefined && source !== PaymentGridSource.Unavailable;
    };

    /**
     * A Lease column spins while this build has a request out for it and no verdict yet - narrower than Finance's
     * `isColumnDecided`, so a column the build never asked about paints blank instead of spinning forever.
     */
    const isColumnAwaitingVerdict = (index: number) => {
        const termMonths = termAt(index);
        if (!termMonths) return false;
        const wasRequested = columnView.requestedColumns.size
            ? columnView.requestedColumns.has(index)
            : columnView.requestedTerms.has(termMonths);
        return wasRequested && verdictFor(index, termMonths) === undefined;
    };

    /**
     * A Lease cell whose ROW belongs to a fan-out still working somewhere is held, even once its own row has answered:
     * `item.isLoading` clears when this row settles, so a three-row edit painted its column one cell at a time. Scoped
     * by round, so a cash-down edit holds only the row it asked about.
     */
    const isCellHeldForRound = (index: number) => {
        const termMonths = termAt(index);
        if (!termMonths || rowCashDown === undefined) return false;
        const heldColumns = columnView.heldColumnsByRow[data.key];
        if (heldColumns) return heldColumns.has(index);
        return Boolean(columnView.heldTermsByRow[data.key]?.has(termMonths));
    };

    const isCellLoading = (item: PaymentGridCellItem, index: number) =>
        columnView.isMasked
            ? Boolean(item.isLoading) || isColumnAwaitingVerdict(index) || isCellHeldForRound(index)
            : Boolean(item.isLoading) ||
              Boolean(isAspPgRedesignEnabled && !isColumnDecided(index)) ||
              isColumnAwaitingOutcome(item, index);

    /** Whether this cell may display its payment. On Lease the column's verdict has veto - a stale term record is not evidence. */
    const isCellShowingPayment = (item: PaymentGridCellItem, index: number) => {
        if (isCellLoading(item, index) || item.isEmpty) return false;
        if (!columnView.isMasked) return true;
        const termMonths = termAt(index);
        if (!termMonths) return false;
        if (decidedColumns[index] !== undefined) return columnView.quotedColumns.has(index);
        return columnView.quotedTerms.has(termMonths as number);
    };

    /**
     * Whether a click may re-point the deal at this cell. On Finance this is master's rule verbatim; only a masked
     * (Lease) grid also requires this build's verdict, since only there can `terms.byId` hold a record the current build
     * never quoted. Finance's own handler/styling mismatch pre-dates this change and is left alone.
     */
    const isGridPersistenceEnabled = useSelector(featureToggleSelectors.isPaymentGridBaggageCompressionEnabled);

    const isCellSelectable = (item: PaymentGridCellItem, index: number) =>
        columnView.isMasked ? isCellShowingPayment(item, index) : !item.isEmpty;

    /** The same deal-type split for the selected-cell styling: Finance keeps master's loading-only test. */
    const showsSelectedStyling = (item: PaymentGridCellItem, index: number) =>
        columnView.isMasked ? isCellShowingPayment(item, index) : !isCellLoading(item, index);

    const handleSelect = (item: PaymentGridCellItem, columnIndex: number) => () => {
        if (!isCellSelectable(item, columnIndex)) return;

        const gridOverrides =
            isPgOverrideIndicatorEnabled && isAspPgRedesignEnabled
                ? offerReduxSelectors.getPaymentGridOverriddenInputs(store.getState() as never, offerType)
                : undefined;
        const isDealsColumn =
            offerReduxSelectors.getResolvedPaymentGridSelectedColumnIndex(store.getState() as never, offerType) === columnIndex;
        const dealScreenOverrides = manualFieldOverridesSelectors.getManualFieldOverrides(store.getState() as never);
        const keepsDealScreenOverride = (fieldKey: string) => isDealsColumn && dealScreenOverrides.includes(fieldKey);

        logNewRelicPageAction('MV:PaymentGrid - Change selected term', {
            mvOfferType: offerType,
            mvFieldEdited: 'mv:payment:grid:selected-term',
            mvDealXgId: dealXgId
        });

        if (rowCashDown !== undefined) {
            dispatch(offerReduxActionCreators.updateDownPaymentImmediate(rowCashDown));
        }
        if (isAspPgRedesignEnabled) {
            dispatch(offerReduxActionCreators.setPaymentGridSelectedColumn(offerType, columnIndex));
            dispatch(offerReduxActionCreators.setPaymentGridSelectedRow(offerType, data.key));
        }
        if (typeof item.id === 'string') {
            /**
             * Marked as grid-originated so selecting a cell re-points the deal without deciding its calculation engine -
             * on Lease that switch dispatches its own payment, costing a second call. The cell's engine travels on this
             * action instead, via forceEngineForSelectedCell.
             *
             * US2085313: no payment when *this grid* quoted the cell — a term record is not enough.
             *
             * `isEmpty` only says a record exists in `terms.byId`, which is merge-only and outlives its grid, so
             * suppressing on that alone let a stale record answer the click: price the grid, edit a fee on the deal
             * screen (only the active term re-prices), then click another cell, and the deal adopted a payment
             * computed before the edit with no spinner and nothing to correct it. The priced-at stamp is the verdict
             * the rule at the top of this file asks for, and it dies with its grid. With the stamp present the deal
             * adopts the rendered numbers and switching options costs no round trip, which is the point of
             * persisting the grid. With the toggle off every selection prices exactly as before.
             */
            const columnTerm = termAt(columnIndex);
            const isTermShared = paymentGridTerms.filter((term) => term != null && term === columnTerm).length > 1;
            const isCellShared =
                isAspPgRedesignEnabled &&
                (isTermShared || paymentGridCashDown.filter((cashDown) => cashDown != null && cashDown === data.val).length > 1);
            const ownRecord = isCellShared
                ? offerReduxSelectors.getPaymentGridCellRecord(
                      store.getState() as never,
                      offerType,
                      columnIndex,
                      data.key,
                      columnTerm ?? undefined,
                      rowCashDown
                  )
                : undefined;

            const servedFromStoredQuote =
                isGridPersistenceEnabled && (Boolean(ownRecord) || (!item.isEmpty && cellPricedAt[item.id] !== undefined));

            const ownColumnOverride = offerReduxSelectors.getOwnColumnOverride(
                store.getState() as never,
                offerType,
                columnIndex,
                columnTerm
            );

            if (ownRecord || (isAspPgRedesignEnabled && isTermShared)) {
                const columnRate = isTermShared
                    ? { sellRate: ownColumnOverride?.sellRate, rateType: ownColumnOverride?.rateType }
                    : undefined;
                dispatch(
                    offerReduxActionCreators.adoptPaymentGridCell(
                        offerType,
                        item.id,
                        ownRecord,
                        ownColumnOverride?.residualInfo as never,
                        columnRate,
                        columnIndex
                    )
                );
            }

            dispatch(
                offerReduxActionCreators.selectedTerm(item.id, {
                    ...(servedFromStoredQuote && { payment: false }),
                    meta: { userIntent: CDL_USER_INTENT.PAYMENT_GRID_CELL_SELECTED }
                })
            );

            if (
                (isExplicitLenderP1Enabled && ownColumnOverride?.residualInfo != null && ownColumnOverride.residualValueChanged) ||
                (gridOverrides && (gridOverrides.residual[columnIndex] || keepsDealScreenOverride('baseResidual')))
            ) {
                recordBaseResidualOverride();
            }
            if (gridOverrides && (gridOverrides.sellRate[columnIndex] || keepsDealScreenOverride('sellRate'))) {
                recordSellRateOverride();
            }
        }
    };

    /**
     * `item.id` is a term id, so two columns holding one term at one cash down produce the SAME id and a selector
     * for either matched both. The `-<column>` suffix is added only where the id repeats, so every id a unique-term
     * grid renders - what testcafe and the e2e page objects select on - is unchanged.
     */
    const duplicatedCellIds = new Set(
        data.items
            .map(({ id }: PaymentGridCellItem) => id)
            .filter((id: string | undefined, index: number, all: (string | undefined)[]) => id != null && all.indexOf(id) !== index)
    );
    const cellTestId = (item: PaymentGridCellItem, index: number) =>
        item.id
            ? `grid-item-row-${item.id}${duplicatedCellIds.has(item.id) ? `-${index}` : ''}`
            : `grid-item-row-empty-column-${index}-row-${data.key}`;
    const markerKey = (item: PaymentGridCellItem, index: number) =>
        `${item.id}${duplicatedCellIds.has(item.id) ? `-${index}` : ''}`;

    return (
        <>
            <StyledGridItem xs={3} className={'payment-grid-item'} sx={{ height: '70px' }}>
                <DownPaymentInputField
                    downPaymentValue={data.val}
                    rowIndex={data.key}
                    // A cash-down input belongs to one row, so on Lease it waits only on that row's own request.
                    isLoading={columnView.isMasked ? columnView.loadingRows.has(data.key) : isLoading}
                />
            </StyledGridItem>
            {!!data &&
                data.items.map((item: PaymentGridCellItem, index) => (
                    <StyledGridItem
                        xs={3}
                        className={'payment-grid-item'}
                        key={index}
                        sx={{ height: '70px' }}
                        alignItems="center"
                        paddingLeft="1%"
                        data-testid={cellTestId(item, index)}
                        onClick={handleSelect(item, index)}
                        $isSelected={item.isSelected && showsSelectedStyling(item, index)}
                        $canSelect={isCellShowingPayment(item, index) && !item.isSelected}
                        $isCalculated={isColumnCalculated(item, index)}
                    >
                        {!hasCellFailure(index) && isCellLoading(item, index) && (
                            <LoadingIndicatorWrapper>
                                <LoadingIndicator data-testid="loading-spinner-terms-col" />
                            </LoadingIndicatorWrapper>
                        )}
                        {isCellShowingPayment(item, index) && (
                            <>
                                {(offerType !== FINANCE || isAspPgRedesignEnabled) && (
                                    <StyledTypography data-testid="monthly-payment-lender-code">{item.lenderCode}</StyledTypography>
                                )}
                                <CalculatedCellRow>
                                    <StyledTypography className="large" data-testid="monthly-payment-title">
                                        {formatDollarsAndCents(item.monthlyPayment)}
                                    </StyledTypography>
                                    {isColumnCalculated(item, index) && (
                                        <Tooltip
                                            id={`calculated-payment-tooltip-${markerKey(item, index)}`}
                                            toolTipContent={CALCULATED_COLUMN_TOOLTIP}
                                            position="bottom"
                                            size="large"
                                        >
                                            <CalculatedCellIconButton
                                                type="button"
                                                aria-label={CALCULATED_COLUMN_TOOLTIP}
                                                data-testid={`calculated-payment-icon-${markerKey(item, index)}`}
                                                onClick={(event) => event.stopPropagation()}
                                            >
                                                <ExclamationTriangleIcon
                                                    fontSize="10.5px"
                                                    color={CALCULATED_COLUMN_ICON_COLOR}
                                                    aria-hidden={true}
                                                />
                                            </CalculatedCellIconButton>
                                        </Tooltip>
                                    )}
                                </CalculatedCellRow>
                            </>
                        )}

                        {/*
                          The redesign removes this state for the deal types it applies to. A cell is either filled,
                          still loading, or in a column that could not be priced, and that column is shown blank with
                          the explanation on its term and sell rate inputs instead. Balloon and Flex Buy are outside
                          the redesign and keep this message, since they can leave a cell empty by design.
                        */}
                        {!isAspPgRedesignEnabled &&
                            !isCellLoading(item, index) &&
                            item.isEmpty &&
                            item.id &&
                            hasTermAndDownpayment(item.id, data.val) && (
                                <StyledTypography data-testid="no-payment-available" className="large">
                                    No Payment Available
                                </StyledTypography>
                            )}
                    </StyledGridItem>
                ))}
        </>
    );
};

export default GridRow;
